import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, paymentsAPI, familyAPI } from '../services/api';
import PartyLanCard from '../components/PartyLanCard';
import WireGuardCard from '../components/WireGuardCard';
import NasStorageCard from '../components/NasStorageCard';
import { User, Settings, Wallet, CreditCard, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';

const formatHours = (hours) => {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return '0m';
  const totalMin = Math.round(h * 60);
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hrs <= 0) return `${mins}m`;
  return mins ? `${hrs}h ${mins}m` : `${hrs}h`;
};

const formatMkd = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0.00 MKD';
  return `${n.toFixed(2)} MKD`;
};

const CREDIT_PACKS = [500, 1000, 2500, 5000];

const Profile = () => {
  const { user } = useAuth();
  const [preferredGPU, setPreferredGPU] = useState('');
  const [topUpAmount, setTopUpAmount] = useState(1000);
  const [inviteLinks, setInviteLinks] = useState({});
  const queryClient = useQueryClient();

  const { data: userStats, isLoading: statsLoading } = useQuery(
    'userStats',
    usersAPI.getUserStats,
    { refetchInterval: 10000 }
  );

  useEffect(() => {
    const next = userStats?.suggested_gpu || userStats?.preferred_gpu_type;
    if (next && !preferredGPU) {
      setPreferredGPU(next);
    }
  }, [userStats, preferredGPU]);

  const { data: subscriptions, isLoading: subsLoading } = useQuery(
    'subscriptions',
    paymentsAPI.getSubscriptions
  );

  const { isLoading: paymentsLoading } = useQuery(
    'payments',
    paymentsAPI.getPayments
  );

  const { data: cashback } = useQuery(
    'cashback',
    paymentsAPI.getCashback,
    { refetchInterval: 10000 }
  );

  const { data: lemon } = useQuery(
    'lemonStatus',
    paymentsAPI.getLemonStatus,
    { staleTime: 60000 }
  );

  const { data: lemonSub } = useQuery(
    'lemonSubscription',
    paymentsAPI.getLemonSubscription,
    { refetchInterval: 15000 }
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('topup') === 'success') {
      toast.success('Payment sent. Wallet updates after Lemon Squeezy confirms.');
      queryClient.invalidateQueries(['userStats', 'payments', 'cashback']);
    }
    if (params.get('sub') === 'success') {
      toast.success('Subscription sent. Access updates after Lemon Squeezy confirms.');
      queryClient.invalidateQueries(['userStats', 'lemonSubscription', 'subscriptions']);
    }
  }, [queryClient]);

  const updateProfileMutation = useMutation(usersAPI.updateProfile, {
    onSuccess: () => {
      queryClient.invalidateQueries('userStats');
      toast.success('Profile updated successfully!');
    },
    onError: (error) => {
      // Handle different error response formats
      let errorMessage = 'Failed to update profile';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle Pydantic validation errors
            errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
          }
        }
      }
      toast.error(errorMessage);
    },
  });

  const topUpMutation = useMutation(paymentsAPI.topUpWallet, {
    onSuccess: (data) => {
      if (data?.checkout_url) {
        toast.success('Opening Lemon Squeezy checkout…');
        window.location.href = data.checkout_url;
        return;
      }
      queryClient.invalidateQueries(['userStats', 'payments', 'cashback']);
    },
    onError: (error) => {
      // Handle different error response formats
      let errorMessage = 'Failed to top up wallet';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle Pydantic validation errors
            errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
          }
        }
      }
      toast.error(errorMessage);
    },
  });

  const subscribeMutation = useMutation(
    (sku) => paymentsAPI.subscribe(sku),
    {
    onSuccess: (data) => {
      if (data?.checkout_url) {
        toast.success('Opening Lemon Squeezy checkout…');
        window.location.href = data.checkout_url;
      }
    },
    onError: (error) => {
      let errorMessage = 'Failed to start subscription';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : 'Failed to start subscription';
      }
      toast.error(errorMessage);
    },
    }
  );

  const inviteMutation = useMutation(
    (seat) => familyAPI.invite(seat),
    {
      onSuccess: (data) => {
        setInviteLinks((prev) => ({ ...prev, [data.family_seat]: data.invite_url }));
        queryClient.invalidateQueries('userStats');
        toast.success(`Invite for ${data.username} is ready — copy it now. It lasts 72 hours.`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Could not issue invite');
      },
    }
  );

  const copyInvite = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied');
    } catch (err) {
      toast.error('Could not copy — select the link instead');
    }
  };

  const handleUpdateProfile = (e) => {
    e.preventDefault();
    if (preferredGPU) {
      updateProfileMutation.mutate({ preferred_gpu_type: preferredGPU });
    }
  };

  const packs = Array.isArray(lemon?.packs) && lemon.packs.length
    ? lemon.packs
    : CREDIT_PACKS.map((credit_mkd) => ({ credit_mkd, pay_mkd: credit_mkd, receipt: '' }));
  const selectedPack = packs.find((p) => Number(p.credit_mkd || p.amount_mkd) === topUpAmount) || packs[1] || packs[0];
  const isFamily = Boolean(user?.family_quad || lemonSub?.family);
  const isFamilyMaster = Boolean(user?.family_master || lemonSub?.family_master);
  const familyName = lemonSub?.family_master_username || userStats?.family_master_username || 'the family master';
  const subQuote = isFamily
    ? (lemon?.family_subscription || lemonSub)
    : (lemon?.subscription || lemonSub);
  const subReceipt = lemonSub?.receipt || subQuote?.receipt || '';
  const canSubscribe = !isFamily || isFamilyMaster;
  const gpuOptions = (userStats?.gpu_options && userStats.gpu_options.length)
    ? userStats.gpu_options
    : (isFamily
      ? [
          { id: 'RTX 4070', label: 'RTX 4070 12 GB' },
          { id: 'RTX 5060 Ti', label: 'RTX 5060 Ti 16 GB' },
          { id: 'RTX 4080', label: 'RTX 4080 16 GB' },
          { id: 'RTX 4090', label: 'RTX 4090 24 GB' },
        ]
      : [
          { id: 'RTX 4060', label: 'RTX 4060 8 GB' },
          { id: 'RTX 4070', label: 'RTX 4070 12 GB' },
          { id: 'RTX 5060 Ti', label: 'RTX 5060 Ti 16 GB' },
          { id: 'RTX 4080', label: 'RTX 4080 16 GB' },
          { id: 'RTX 4090', label: 'RTX 4090 24 GB' },
        ]);

  const handleTopUp = (e) => {
    e.preventDefault();
    const credit = Number(selectedPack?.credit_mkd || selectedPack?.amount_mkd || topUpAmount);
    if (credit >= 500) {
      topUpMutation.mutate(credit);
    }
  };

  if (statsLoading || subsLoading || paymentsLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-white mb-2">Profile & Settings</h1>
        <p className="text-white text-opacity-80">
          Manage your account and gaming preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Stats */}
          <div className="gaming-card rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Profile Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-white text-opacity-60 text-sm">Username</p>
                <p className="text-white font-semibold">{user?.username}</p>
              </div>
              <div>
                <p className="text-white text-opacity-60 text-sm">Email</p>
                <p className="text-white font-semibold">{user?.email}</p>
              </div>
              <div>
                <p className="text-white text-opacity-60 text-sm">Member Since</p>
                <p className="text-white font-semibold">
                  {userStats?.member_since ? new Date(userStats.member_since).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-white text-opacity-60 text-sm">Last Login</p>
                <p className="text-white font-semibold">
                  {userStats?.last_login ? new Date(userStats.last_login).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            {(userStats?.family_members || []).length > 0 && (
              <div className="mt-5 pt-4 border-t border-white/10">
                <p className="text-white font-medium text-sm mb-2">Family seats (one GPU)</p>
                <ul className="space-y-3 text-sm text-white/80">
                  {userStats.family_members.map((m) => (
                    <li key={m.username} className="space-y-1">
                      <div>
                        Seat {m.family_seat}: {m.username}
                        {m.family_master ? ' — master' : ' — member'}
                        {m.you ? ' (you)' : ''}
                        <span className="text-white/45"> · {m.email}</span>
                        {!m.family_master && m.invite_outstanding ? (
                          <span className="text-amber-200/80"> · invite outstanding</span>
                        ) : null}
                        {!m.family_master && m.claimed ? (
                          <span className="text-white/45"> · claimed</span>
                        ) : null}
                      </div>
                      {isFamilyMaster && !m.family_master ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => inviteMutation.mutate(m.family_seat)}
                            disabled={inviteMutation.isLoading}
                            className="text-xs px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-white disabled:opacity-50"
                          >
                            {inviteLinks[m.family_seat] ? 'New 72h invite' : 'Issue 72h invite'}
                          </button>
                          {inviteLinks[m.family_seat] ? (
                            <button
                              type="button"
                              onClick={() => copyInvite(inviteLinks[m.family_seat])}
                              className="text-xs px-3 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              Copy link
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      {isFamilyMaster && inviteLinks[m.family_seat] ? (
                        <p className="text-xs text-white/50 break-all">{inviteLinks[m.family_seat]}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {isFamilyMaster ? (
                  <p className="text-xs text-white/50 mt-3">
                    Send the invite to that person. Issuing a new link replaces the old one and
                    invalidates the previous password for that seat.
                  </p>
                ) : null}
              </div>
            )}
          </div>

          {/* Gaming Preferences */}
          <div className="gaming-card rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Gaming Preferences
            </h3>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Preferred GPU Type
                </label>
                <select
                  value={preferredGPU || userStats?.suggested_gpu || userStats?.preferred_gpu_type || ''}
                  onChange={(e) => setPreferredGPU(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {(gpuOptions).map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label || opt.id}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-white/55 mt-2">
                  {isFamily
                    ? 'Family lists 12 GB 4070-class cards and above. Four seats stream Full HD.'
                    : 'Solo lists 8 GB Full HD class and above. You can still pick a stronger card.'}
                </p>
              </div>
              <button
                type="submit"
                disabled={updateProfileMutation.isLoading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {updateProfileMutation.isLoading ? 'Updating...' : 'Update Preferences'}
              </button>
            </form>
          </div>

          {/* Gaming Statistics */}
          <div className="gaming-card rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Gaming Statistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-white bg-opacity-5 rounded-lg">
                <p className="text-2xl font-bold text-blue-400">
                  {formatHours(userStats?.total_hours_played)}
                </p>
                <p className="text-white text-opacity-60">Total Hours Played</p>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-5 rounded-lg">
                <p className="text-2xl font-bold text-green-400">
                  {formatMkd(userStats?.total_spent)}
                </p>
                <p className="text-white text-opacity-60">GPU Rent Spent</p>
              </div>
            </div>
            {userStats?.usage_mix?.buckets && (
              <div className="mt-4 space-y-2">
                <p className="text-white text-sm font-medium">How you played</p>
                {[
                  ['full4', 'Wait/party with 4'],
                  ['underfill3', 'Started with 3'],
                  ['underfill2', 'Started with 2'],
                  ['solo_1to1', 'Solo 1:1'],
                  ['family', 'Family private'],
                ].map(([key, label]) => {
                  const row = userStats.usage_mix.buckets[key] || {};
                  const hours = Number(row.hours || 0);
                  const max = Math.max(
                    0.001,
                    ...Object.values(userStats.usage_mix.buckets || {}).map((b) => Number(b.hours || 0))
                  );
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs text-white/60">
                        <span>{label}</span>
                        <span>
                          {formatHours(hours)} · {row.sessions || 0} sessions
                        </span>
                      </div>
                      <div className="h-1.5 rounded bg-white/10 overflow-hidden">
                        <div
                          className="h-full bg-sky-400"
                          style={{ width: `${Math.min(100, (hours / max) * 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-amber-200/80 mt-2">
                  Skip-wait hours {formatHours(userStats.usage_mix.wait_skip_hours)} ·
                  next-month surcharge about {formatMkd(userStats.usage_mix.next_month_surcharge_mkd)}
                  (capped, not a second GPU bill).
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="gaming-card rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Wallet className="h-5 w-5 mr-2" />
              Wallet
            </h3>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-400 mb-2">
                {formatMkd(userStats?.play_credit ?? cashback?.wallet_balance)}
              </p>
              <p className="text-white text-opacity-60 text-sm">
                {isFamily
                  ? `Shared family GPU credit (top-ups minus rent on ${familyName}). Max 20,000 MKD.`
                  : 'GPU credit (top-ups minus rent). Max 20,000 MKD.'}
              </p>
            </div>
          </div>

          <div className="gaming-card rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Monthly subscription</h3>
            {userStats?.subscription_active || lemonSub?.active ? (
              <>
                <p className="text-green-400 font-medium">
                  {isFamily
                    ? `Active family access — €8 / month, one GPU, four seats${familyName ? ` (${familyName})` : ''}. Weekly slice €2 when you launched, €1.25 when the week was idle (credited back to the GPU wallet at renewal, up to €3/mo).`
                    : 'Active — €2 / month to launch a GPU. Weekly slice €0.50 when you launched, €0.25 when idle (credited back to the wallet at renewal, up to €1/mo).'}
                </p>
                {subReceipt ? (
                  <p className="text-white text-opacity-60 text-xs mt-2">{subReceipt}</p>
                ) : null}
              </>
            ) : !canSubscribe ? (
              <>
                <p className="text-white text-opacity-70 text-sm mb-3">
                  Family access is billed on {familyName}. They subscribe once for all four seats.
                </p>
                {subReceipt ? (
                  <p className="text-indigo-200 text-sm font-medium mb-3">{subReceipt}</p>
                ) : null}
              </>
            ) : (
              <>
                <p className="text-white text-opacity-70 text-sm mb-3">
                  {isFamily
                    ? '€8 / month family access (4 seats, one GPU). Each week counts on its own: a week with any GPU launch bills the full €2 slice, an idle week credits €0.75 back to the shared wallet at renewal (up to €3/mo). GPU rent is extra, by the second, from Vast reserve (boot included) on the shared family wallet.'
                    : 'Choose Solo (€2, one player) or Family (€8, four seats on one GPU). After Family pays, the platform mints three seat logins — you send each person a 72-hour invite. GPU rent is extra, by the second, from Vast reserve (boot included).'}
                </p>
                {subReceipt ? (
                  <p className="text-indigo-200 text-sm font-medium mb-3">{subReceipt}</p>
                ) : null}
                {isFamily ? (
                  <button
                    type="button"
                    onClick={() => subscribeMutation.mutate('family')}
                    disabled={subscribeMutation.isLoading}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {subscribeMutation.isLoading
                      ? 'Opening checkout…'
                      : 'Subscribe Family — 8.00 EUR / month'}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => subscribeMutation.mutate('solo')}
                      disabled={subscribeMutation.isLoading}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {subscribeMutation.isLoading
                        ? 'Opening checkout…'
                        : 'Subscribe Solo — 2.00 EUR / month'}
                    </button>
                    <button
                      type="button"
                      onClick={() => subscribeMutation.mutate('family')}
                      disabled={subscribeMutation.isLoading}
                      className="w-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Subscribe Family — 8.00 EUR / month · 4 seats
                    </button>
                  </div>
                )}
                {!lemon?.subscription_configured && (
                  <p className="text-white text-opacity-50 text-xs mt-2">
                    {lemon?.hint || 'Monthly product is not configured on the server yet.'}
                  </p>
                )}
              </>
            )}
          </div>

          <PartyLanCard isFamily={isFamily} />

          <div className="gaming-card rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Add funds (Lemon Squeezy)
            </h3>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {packs.map((pack) => {
                const credit = Number(pack.credit_mkd || pack.amount_mkd);
                return (
                  <button
                    key={credit}
                    type="button"
                    onClick={() => setTopUpAmount(credit)}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      topUpAmount === credit
                        ? 'bg-purple-600 text-white'
                        : 'bg-white bg-opacity-10 text-white'
                    }`}
                  >
                    {credit.toLocaleString()} MKD GPU
                  </button>
                );
              })}
            </div>
            <form onSubmit={handleTopUp} className="space-y-4">
              {selectedPack?.receipt ? (
                <p className="text-indigo-200 text-sm font-medium">{selectedPack.receipt}</p>
              ) : null}
              <button
                type="submit"
                disabled={topUpMutation.isLoading}
                className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {topUpMutation.isLoading
                  ? 'Opening checkout…'
                  : selectedPack?.pay_eur
                    ? `Pay ${Number(selectedPack.pay_eur).toFixed(2)} EUR`
                    : selectedPack?.pay_mkd
                    ? `Pay ${Number(selectedPack.pay_mkd).toLocaleString()} MKD`
                    : 'Pay with Lemon Squeezy'}
              </button>
            </form>
            <p className="text-white text-opacity-60 text-xs mt-2">
              {lemon?.configured
                ? 'Lemon charges EUR. Pack size is MKD for GPU rent (NBRM rate + FX cover in the hourly price). The wallet charges from GPU start, including boot — Vast bills us then. Solo pays the full box; family uses the shared wallet; party splits the hour.'
                : (lemon?.hint || 'Lemon Squeezy is not configured on the server yet.')}
            </p>
          </div>

          {/* Active Subscription */}
          {subscriptions && subscriptions.some((sub) => sub.status === 'active') && (
            <div className="gaming-card rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Active Subscription</h3>
              {subscriptions.find(sub => sub.status === 'active') ? (
                <div className="space-y-2">
                  {subscriptions
                    .filter(sub => sub.status === 'active')
                    .map((sub) => (
                      <div key={sub.id} className="p-3 bg-white bg-opacity-5 rounded-lg">
                        <p className="text-white font-medium">{sub.package_name}</p>
                        {sub.total_hours > 0 ? (
                          <>
                            <p className="text-white text-opacity-60 text-sm">
                              {sub.hours_remaining} hours remaining
                            </p>
                            <p className="text-white text-opacity-60 text-sm">
                              {(sub.utilization_percentage || 0).toFixed(1)}% utilized
                            </p>
                          </>
                        ) : (
                          <p className="text-white text-opacity-60 text-sm">Monthly access</p>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-white text-opacity-60 text-center py-4">
                  No active subscription
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <NasStorageCard />

      <WireGuardCard />
    </div>
  );
};

export default Profile;
